import React, { useState, Component } from 'react';
import Layout from '../../components/Layout';
import { useNavigate } from 'react-router-dom';
import { GraduationCapIcon, SearchIcon, FilterIcon, ChevronDownIcon, BuildingIcon, UsersIcon, CalendarIcon, BarChart3Icon, AlertCircleIcon, BrainIcon, TrendingUpIcon, ArrowRightIcon, DownloadIcon, EyeIcon, MoreHorizontalIcon, ClockIcon, SparklesIcon, TagIcon, ArrowUpDownIcon } from 'lucide-react';
// Sample program data
const programsData = [{
  id: 1,
  name: 'Master of Business Administration',
  university: 'UAE Business School',
  universityId: 1,
  type: "Master's",
  duration: '2 Years',
  applications: 48,
  trend: '+12%',
  status: 'Active',
  skillGap: 'Low',
  nextIntake: 'Fall 2023',
  lastUpdated: '2023-06-15',
  description: 'Our MBA program is designed to develop the skills required for careers in business and management. The program integrates theoretical and practical knowledge of business with an international perspective.',
  tags: ['High Salary', 'In-Demand'],
  flagged: false,
  healthScore: 92,
  pendingReviews: 8,
  location: 'Dubai'
}, {
  id: 2,
  name: 'Bachelor of Computer Science',
  university: 'Emirates Institute of Technology',
  universityId: 2,
  type: "Bachelor's",
  duration: '4 Years',
  applications: 112,
  trend: '+28%',
  status: 'Active',
  skillGap: 'Medium',
  nextIntake: 'Fall 2023',
  lastUpdated: '2023-06-12',
  description: 'The Bachelor of Computer Science program provides students with a comprehensive foundation in computer science principles, programming languages, and software development methodologies.',
  tags: ['High Salary', 'In-Demand', 'Fast Completion'],
  flagged: false,
  healthScore: 88,
  pendingReviews: 15,
  location: 'Abu Dhabi'
}, {
  id: 3,
  name: 'Master of Public Administration',
  university: 'UAE Business School',
  universityId: 1,
  type: "Master's",
  duration: '2 Years',
  applications: 27,
  trend: '-5%',
  status: 'Active',
  skillGap: 'High',
  nextIntake: 'Spring 2024',
  lastUpdated: '2023-06-10',
  description: 'The Master of Public Administration program prepares students for leadership roles in government, non-profit organizations, and public service.',
  tags: ['Flexible Schedule'],
  flagged: true,
  healthScore: 75,
  pendingReviews: 6,
  location: 'Dubai'
}, {
  id: 4,
  name: 'PhD in Data Science',
  university: 'Emirates Institute of Technology',
  universityId: 2,
  type: 'Doctorate',
  duration: '3 Years',
  applications: 15,
  trend: '+8%',
  status: 'Pending Approval',
  skillGap: 'Low',
  nextIntake: 'Spring 2024',
  lastUpdated: '2023-06-05',
  description: 'This doctoral program prepares researchers and professionals to advance the field of data science through original research and innovative applications.',
  tags: ['High Salary', 'In-Demand'],
  flagged: false,
  healthScore: 85,
  pendingReviews: 0,
  location: 'Abu Dhabi'
}, {
  id: 5,
  name: 'Bachelor of Nursing',
  university: 'Dubai Medical College',
  universityId: 4,
  type: "Bachelor's",
  duration: '4 Years',
  applications: 62,
  trend: '+18%',
  status: 'Active',
  skillGap: 'Critical',
  nextIntake: 'Fall 2023',
  lastUpdated: '2023-06-08',
  description: 'The Bachelor of Nursing program prepares students for careers in healthcare with a focus on patient care, health promotion, and disease prevention.',
  tags: ['In-Demand', 'Fast Completion'],
  flagged: true,
  healthScore: 78,
  pendingReviews: 12,
  location: 'Dubai'
}, {
  id: 6,
  name: 'Master of Finance',
  university: 'Abu Dhabi School of Management',
  universityId: 5,
  type: "Master's",
  duration: '1.5 Years',
  applications: 35,
  trend: '+2%',
  status: 'Active',
  skillGap: 'Medium',
  nextIntake: 'Fall 2023',
  lastUpdated: '2023-06-07',
  description: 'The Master of Finance program provides advanced knowledge and skills in financial analysis, investment management, and corporate finance.',
  tags: ['High Salary'],
  flagged: false,
  healthScore: 82,
  pendingReviews: 7,
  location: 'Abu Dhabi'
}, {
  id: 7,
  name: 'Bachelor of Architecture',
  university: 'Sharjah American University',
  universityId: 3,
  type: "Bachelor's",
  duration: '5 Years',
  applications: 45,
  trend: '+5%',
  status: 'Active',
  skillGap: 'Low',
  nextIntake: 'Fall 2023',
  lastUpdated: '2023-06-09',
  description: 'The Bachelor of Architecture program prepares students for careers in architectural design, urban planning, and sustainable development.',
  tags: ['High Salary', 'In-Demand'],
  flagged: false,
  healthScore: 90,
  pendingReviews: 9,
  location: 'Sharjah'
}, {
  id: 8,
  name: 'Master of Education',
  university: 'Zayed University',
  universityId: 6,
  type: "Master's",
  duration: '2 Years',
  applications: 28,
  trend: '-3%',
  status: 'Under Review',
  skillGap: 'High',
  nextIntake: 'Spring 2024',
  lastUpdated: '2023-06-03',
  description: 'The Master of Education program prepares educators for leadership roles in schools, universities, and educational organizations.',
  tags: ['Flexible Schedule'],
  flagged: true,
  healthScore: 73,
  pendingReviews: 5,
  location: 'Abu Dhabi'
}];
// Program Status Badge Component
const StatusBadge = ({
  status
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-yellow-100 text-yellow-800';
      case 'Pending Approval':
        return 'bg-blue-100 text-blue-800';
      case 'Inactive':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor()}`} data-id="element-1609">
      {status}
    </span>;
};
// Trend Indicator Component
const TrendIndicator = ({
  trend
}) => {
  const isPositive = trend.startsWith('+');
  return <span className={`inline-flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1610">
      {isPositive ? <TrendingUpIcon className="w-4 h-4 mr-1" data-id="element-1611" /> : <TrendingUpIcon className="w-4 h-4 mr-1 transform rotate-180" data-id="element-1612" />}
      {trend}
    </span>;
};
// Skill Gap Indicator Component
const SkillGapIndicator = ({
  gap
}) => {
  const getGapColor = () => {
    switch (gap) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getGapColor()}`} data-id="element-1613">
      {gap}
    </span>;
};
// Health Score Component
const HealthScore = ({
  score
}) => {
  const getScoreColor = () => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };
  return <div className="flex items-center" data-id="element-1614">
      <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden mr-2" data-id="element-1615">
        <div className={`h-full ${score >= 90 ? 'bg-green-500' : score >= 80 ? 'bg-blue-500' : score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`} style={{
        width: `${score}%`
      }} data-id="element-1616"></div>
      </div>
      <span className={`text-sm font-medium ${getScoreColor()}`} data-id="element-1617">{score}</span>
    </div>;
};
// Program Detail Modal Component
const ProgramDetailModal = ({
  isOpen,
  onClose,
  program
}) => {
  if (!isOpen || !program) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="program-detail-modal" role="dialog" aria-modal="true" data-id="element-1618">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1619">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose} data-id="element-1620"></div>
        {/* Modal positioning */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1621">
          &#8203;
        </span>
        {/* Modal content */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full" data-id="element-1622">
          <div className="bg-primary-400 px-6 py-4 flex justify-between items-center" data-id="element-1623">
            <h3 className="text-lg font-medium text-white" id="modal-title" data-id="element-1624">
              Program Details
            </h3>
            <button onClick={onClose} className="text-white hover:text-gray-200" data-id="element-1625">
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1626">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-1627" />
              </svg>
            </button>
          </div>
          <div className="bg-white px-6 py-4" data-id="element-1628">
            <div className="flex items-center mb-6" data-id="element-1629">
              <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mr-4" data-id="element-1630">
                <GraduationCapIcon className="h-8 w-8" data-id="element-1631" />
              </div>
              <div data-id="element-1632">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-1633">
                  {program.name}
                </h2>
                <div className="flex items-center mt-1" data-id="element-1634">
                  <BuildingIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-1635" />
                  <span className="text-gray-600" data-id="element-1636">{program.university}</span>
                  <span className="mx-2 text-gray-300" data-id="element-1637">•</span>
                  <StatusBadge status={program.status} data-id="element-1638" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-1639">
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1640">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1641">Type</div>
                <div className="text-lg font-medium" data-id="element-1642">{program.type}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1643">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1644">Duration</div>
                <div className="text-lg font-medium" data-id="element-1645">{program.duration}</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg" data-id="element-1646">
                <div className="text-sm text-gray-500 mb-1" data-id="element-1647">Health Score</div>
                <div className="text-lg font-medium flex items-center" data-id="element-1648">
                  <HealthScore score={program.healthScore} data-id="element-1649" />
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1650">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1651">
                Description
              </h4>
              <p className="text-gray-700" data-id="element-1652">{program.description}</p>
            </div>
            <div className="mb-6" data-id="element-1653">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1654">
                Applications & Trends
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1655">
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1656">
                  <div className="flex items-center" data-id="element-1657">
                    <div className="p-2 bg-primary-50 rounded-full mr-3" data-id="element-1658">
                      <UsersIcon className="h-5 w-5 text-primary-500" data-id="element-1659" />
                    </div>
                    <div data-id="element-1660">
                      <div className="text-2xl font-semibold" data-id="element-1661">
                        {program.applications}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1662">Applications</div>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1663">
                  <div className="flex items-center" data-id="element-1664">
                    <div className="p-2 bg-primary-50 rounded-full mr-3" data-id="element-1665">
                      <TrendingUpIcon className="h-5 w-5 text-primary-500" data-id="element-1666" />
                    </div>
                    <div data-id="element-1667">
                      <div className="text-2xl font-semibold" data-id="element-1668">
                        <TrendIndicator trend={program.trend} data-id="element-1669" />
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1670">
                        Application Trend
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-md p-4" data-id="element-1671">
                  <div className="flex items-center" data-id="element-1672">
                    <div className="p-2 bg-yellow-50 rounded-full mr-3" data-id="element-1673">
                      <ClockIcon className="h-5 w-5 text-yellow-500" data-id="element-1674" />
                    </div>
                    <div data-id="element-1675">
                      <div className="text-2xl font-semibold" data-id="element-1676">
                        {program.pendingReviews}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-1677">
                        Pending Reviews
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-6" data-id="element-1678">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1679">
                Next Intake
              </h4>
              <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-50 text-primary-700" data-id="element-1680">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-1681" />
                {program.nextIntake}
              </div>
            </div>
            <div className="mb-6" data-id="element-1682">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1683">
                Program Tags
              </h4>
              <div className="flex flex-wrap gap-2" data-id="element-1684">
                {program.tags.map((tag, index) => <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-50 text-blue-700" data-id="element-1685">
                    <TagIcon className="w-4 h-4 mr-1" data-id="element-1686" />
                    {tag}
                  </span>)}
              </div>
            </div>
            <div className="mb-6" data-id="element-1687">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1688">
                Skill Gap Analysis
              </h4>
              <div className="flex items-center mb-3" data-id="element-1689">
                <SkillGapIndicator gap={program.skillGap} data-id="element-1690" />
                <span className="ml-2 text-sm text-gray-700" data-id="element-1691">
                  {program.skillGap === 'Critical' ? 'Critical skill gap detected' : program.skillGap === 'High' ? 'High skill gap detected' : program.skillGap === 'Medium' ? 'Medium skill gap detected' : 'Low skill gap detected'}
                </span>
              </div>
              {(program.skillGap === 'Critical' || program.skillGap === 'High') && <div className="p-4 bg-red-50 border border-red-100 rounded-lg" data-id="element-1692">
                  <div className="flex items-start" data-id="element-1693">
                    <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1694" />
                    <div data-id="element-1695">
                      <h3 className="font-medium text-red-800 mb-1" data-id="element-1696">
                        Curriculum Alignment Issue
                      </h3>
                      <p className="text-sm text-red-700" data-id="element-1697">
                        This program's curriculum shows significant misalignment
                        with current market needs. AI analysis suggests updating
                        the program content to address skill gaps in{' '}
                        {program.name.includes('Public') ? 'digital governance and policy analytics' : program.name.includes('Education') ? 'educational technology and remote learning methodologies' : 'emerging technologies and industry practices'}
                        .
                      </p>
                    </div>
                  </div>
                </div>}
              {program.trend.startsWith('-') && <div className="mt-4 p-4 bg-orange-50 border border-orange-100 rounded-lg" data-id="element-1698">
                  <div className="flex items-start" data-id="element-1699">
                    <TrendingUpIcon className="w-5 h-5 text-orange-500 mr-3 flex-shrink-0 mt-0.5 transform rotate-180" data-id="element-1700" />
                    <div data-id="element-1701">
                      <h3 className="font-medium text-orange-800 mb-1" data-id="element-1702">
                        Declining Application Trend
                      </h3>
                      <p className="text-sm text-orange-700" data-id="element-1703">
                        Applications for this program have declined by{' '}
                        {program.trend.substring(1)}. AI analysis suggests this
                        may be due to{' '}
                        {program.skillGap === 'High' || program.skillGap === 'Critical' ? 'curriculum misalignment with market needs' : 'increasing competition from similar programs at other institutions'}
                        .
                      </p>
                    </div>
                  </div>
                </div>}
            </div>
            <div className="mb-6" data-id="element-1704">
              <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1705">
                AI Recommendations
              </h4>
              <div className="p-4 bg-primary-50 border border-primary-100 rounded-lg" data-id="element-1706">
                <div className="flex items-start" data-id="element-1707">
                  <SparklesIcon className="w-5 h-5 text-primary-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1708" />
                  <div data-id="element-1709">
                    <p className="text-sm text-primary-700" data-id="element-1710">
                      {program.skillGap === 'Critical' || program.skillGap === 'High' ? `We recommend a comprehensive curriculum review for this program. Consider incorporating ${program.name.includes('Business') ? 'digital transformation, data analytics, and sustainable business practices' : program.name.includes('Computer') ? 'cloud computing, cybersecurity, and artificial intelligence' : program.name.includes('Nursing') ? 'telehealth technologies, pandemic preparedness, and healthcare informatics' : 'emerging industry practices and technologies'} to address current skill gaps.` : program.trend.startsWith('-') ? `Consider refreshing marketing materials and outreach strategies to highlight program strengths and career outcomes. Engage with industry partners to ensure program visibility and relevance.` : `This program is performing well. Continue monitoring market trends and consider minor curriculum updates to maintain alignment with industry needs.`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-6 py-4 flex justify-between" data-id="element-1711">
            <button className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-100" onClick={onClose} data-id="element-1712">
              Close
            </button>
            <div className="flex space-x-2" data-id="element-1713">
              <button className="px-4 py-2 border border-primary-300 bg-primary-50 rounded-md text-sm font-medium text-primary-700 hover:bg-primary-100" data-id="element-1714">
                View Applications
              </button>
              <button className="px-4 py-2 bg-primary-500 text-white rounded-md text-sm font-medium hover:bg-primary-600" data-id="element-1715">
                View Full Analytics
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const Programs = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [universityFilter, setUniversityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [skillGapFilter, setSkillGapFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  // Filter programs based on search query and filters
  const filteredPrograms = programsData.filter(program => program.name.toLowerCase().includes(searchQuery.toLowerCase()) || program.university.toLowerCase().includes(searchQuery.toLowerCase())).filter(program => statusFilter === 'all' || program.status === statusFilter).filter(program => universityFilter === 'all' || program.university === universityFilter).filter(program => typeFilter === 'all' || program.type === typeFilter).filter(program => skillGapFilter === 'all' || program.skillGap === skillGapFilter).sort((a, b) => {
    const factor = sortOrder === 'asc' ? 1 : -1;
    switch (sortBy) {
      case 'name':
        return factor * a.name.localeCompare(b.name);
      case 'university':
        return factor * a.university.localeCompare(b.university);
      case 'applications':
        return factor * (a.applications - b.applications);
      case 'healthScore':
        return factor * (a.healthScore - b.healthScore);
      default:
        return 0;
    }
  });
  // Get unique universities for filter
  const universities = [...new Set(programsData.map(program => program.university))];
  // Get unique program types for filter
  const programTypes = [...new Set(programsData.map(program => program.type))];
  // Handle sort toggle
  const toggleSort = field => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };
  // Open program detail modal
  const openDetailModal = program => {
    setSelectedProgram(program);
    setIsDetailModalOpen(true);
  };
  // Close program detail modal
  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProgram(null);
  };
  return <Layout title="Programs Management" persona="Platform Admin" data-id="element-1716">
      <div className="space-y-6" data-id="element-1717">
        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1718">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1719">
            <div className="flex items-start justify-between" data-id="element-1720">
              <div data-id="element-1721">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-1722">
                  Total Programs
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1723">
                  {programsData.length}
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1724">
                  {programsData.filter(p => p.status === 'Active').length}{' '}
                  active
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1725">
                <GraduationCapIcon className="w-6 h-6 text-primary-500" data-id="element-1726" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1727">
            <div className="flex items-start justify-between" data-id="element-1728">
              <div data-id="element-1729">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-1730">
                  Total Applications
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1731">
                  {programsData.reduce((sum, program) => sum + program.applications, 0)}
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1732">
                  {programsData.reduce((sum, program) => sum + program.pendingReviews, 0)}{' '}
                  pending reviews
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1733">
                <UsersIcon className="w-6 h-6 text-primary-500" data-id="element-1734" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1735">
            <div className="flex items-start justify-between" data-id="element-1736">
              <div data-id="element-1737">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-1738">
                  Skill Gap Alert
                </h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1739">
                  {programsData.filter(p => p.skillGap === 'Critical' || p.skillGap === 'High').length}
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1740">
                  programs with high skill gaps
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1741">
                <BrainIcon className="w-6 h-6 text-primary-500" data-id="element-1742" />
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1743">
            <div className="flex items-start justify-between" data-id="element-1744">
              <div data-id="element-1745">
                <h3 className="font-medium text-gray-800 mb-2" data-id="element-1746">Next Intake</h3>
                <div className="text-3xl font-bold text-primary-500" data-id="element-1747">
                  {programsData.filter(p => p.nextIntake === 'Fall 2023').length}
                </div>
                <p className="text-sm text-gray-500 mt-1" data-id="element-1748">
                  programs in Fall 2023
                </p>
              </div>
              <div className="p-3 bg-primary-50 rounded-full" data-id="element-1749">
                <CalendarIcon className="w-6 h-6 text-primary-500" data-id="element-1750" />
              </div>
            </div>
          </div>
        </div>
        {/* Action Bar */}
        <div className="flex flex-wrap justify-between items-center gap-4" data-id="element-1751">
          <div className="flex-grow max-w-2xl" data-id="element-1752">
            <div className="relative" data-id="element-1753">
              <input type="text" placeholder="Search programs by name or university..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1754" />
              <SearchIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" data-id="element-1755" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3" data-id="element-1756">
            {/* Status Filter */}
            <div className="relative" data-id="element-1757">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1758">
                <option value="all" data-id="element-1759">All Statuses</option>
                <option value="Active" data-id="element-1760">Active</option>
                <option value="Under Review" data-id="element-1761">Under Review</option>
                <option value="Pending Approval" data-id="element-1762">Pending Approval</option>
                <option value="Inactive" data-id="element-1763">Inactive</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1764" />
            </div>
            {/* University Filter */}
            <div className="relative" data-id="element-1765">
              <select value={universityFilter} onChange={e => setUniversityFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1766">
                <option value="all" data-id="element-1767">All Universities</option>
                {universities.map((university, index) => <option key={index} value={university} data-id="element-1768">
                    {university}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1769" />
            </div>
            {/* Program Type Filter */}
            <div className="relative" data-id="element-1770">
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1771">
                <option value="all" data-id="element-1772">All Types</option>
                {programTypes.map((type, index) => <option key={index} value={type} data-id="element-1773">
                    {type}
                  </option>)}
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1774" />
            </div>
            {/* Skill Gap Filter */}
            <div className="relative" data-id="element-1775">
              <select value={skillGapFilter} onChange={e => setSkillGapFilter(e.target.value)} className="pl-3 pr-8 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" data-id="element-1776">
                <option value="all" data-id="element-1777">All Skill Gaps</option>
                <option value="Critical" data-id="element-1778">Critical</option>
                <option value="High" data-id="element-1779">High</option>
                <option value="Medium" data-id="element-1780">Medium</option>
                <option value="Low" data-id="element-1781">Low</option>
              </select>
              <ChevronDownIcon className="absolute right-3 top-2.5 w-4 h-4 text-gray-500 pointer-events-none" data-id="element-1782" />
            </div>
            {/* Export Button */}
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50" data-id="element-1783">
              <DownloadIcon className="h-4 w-4 mr-2 text-gray-500" data-id="element-1784" />
              Export
            </button>
          </div>
        </div>
        {/* Programs Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1785">
          <div className="overflow-x-auto" data-id="element-1786">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1787">
              <thead className="bg-gray-50" data-id="element-1788">
                <tr data-id="element-1789">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('name')} data-id="element-1790">
                    <div className="flex items-center" data-id="element-1791">
                      Program
                      {sortBy === 'name' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-1792" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('university')} data-id="element-1793">
                    <div className="flex items-center" data-id="element-1794">
                      University
                      {sortBy === 'university' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-1795" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1796">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('applications')} data-id="element-1797">
                    <div className="flex items-center" data-id="element-1798">
                      Applications
                      {sortBy === 'applications' && <ArrowUpDownIcon className="w-4 h-4 ml-1" data-id="element-1799" />}
                    </div>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1800">
                    Trend
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1801">
                    Skill Gap
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1802">
                    Next Intake
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1803">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1804">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1805">
                {filteredPrograms.map(program => <tr key={program.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/admin/programs/${program.id}`)} data-id="element-1806">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1807">
                      <div className="flex items-center" data-id="element-1808">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center" data-id="element-1809">
                          <GraduationCapIcon className="h-5 w-5" data-id="element-1810" />
                        </div>
                        <div className="ml-4" data-id="element-1811">
                          <div className="text-sm font-medium text-gray-900" data-id="element-1812">
                            {program.name}
                          </div>
                          <div className="text-sm text-gray-500" data-id="element-1813">
                            {program.university}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1814">
                      <div className="flex items-center text-sm text-gray-900" data-id="element-1815">
                        <BuildingIcon className="h-4 w-4 text-gray-400 mr-1" data-id="element-1816" />
                        {program.university}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1817">
                        {program.location}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1818">
                      {program.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1819">
                      <div className="text-sm text-gray-900" data-id="element-1820">
                        {program.applications}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-1821">
                        {program.pendingReviews} pending
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1822">
                      <TrendIndicator trend={program.trend} data-id="element-1823" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1824">
                      <SkillGapIndicator gap={program.skillGap} data-id="element-1825" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1826">
                      {program.nextIntake}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-1827">
                      <StatusBadge status={program.status} data-id="element-1828" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1829">
                      <div className="flex justify-end space-x-2" data-id="element-1830">
                        <button onClick={() => navigate(`/admin/programs/${program.id}`)} className="text-gray-600 hover:text-gray-900" data-id="element-1831">
                          <MoreHorizontalIcon className="h-5 w-5" data-id="element-1832" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          {filteredPrograms.length === 0 && <div className="text-center py-12" data-id="element-1833">
              <GraduationCapIcon className="mx-auto h-12 w-12 text-gray-300" data-id="element-1834" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1835">
                No programs found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-1836">
                Try adjusting your search or filter criteria
              </p>
            </div>}
        </div>
        {/* AI Insights */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1837">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center" data-id="element-1838">
            <h2 className="text-lg font-medium text-gray-800" data-id="element-1839">
              AI Insights & Recommendations
            </h2>
            <button onClick={() => navigate('/admin/analytics')} className="text-sm text-primary-500 hover:underline flex items-center" data-id="element-1840">
              View Full Analytics
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-1841" />
            </button>
          </div>
          <div className="p-6" data-id="element-1842">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1843">
              <div className="p-4 bg-red-50 rounded-lg border border-red-100" data-id="element-1844">
                <div className="flex items-start" data-id="element-1845">
                  <AlertCircleIcon className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1846" />
                  <div data-id="element-1847">
                    <h3 className="font-medium text-red-800 mb-1" data-id="element-1848">
                      Critical Skill Gaps
                    </h3>
                    <p className="text-sm text-red-700 mb-3" data-id="element-1849">
                      3 programs have critical skill gaps that require immediate
                      attention. The Bachelor of Nursing program at Dubai
                      Medical College shows the most significant misalignment
                      with current healthcare industry needs.
                    </p>
                    <button className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors flex items-center" data-id="element-1850">
                      View Programs
                      <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1851" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-100" data-id="element-1852">
                <div className="flex items-start" data-id="element-1853">
                  <TrendingUpIcon className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5 transform rotate-180" data-id="element-1854" />
                  <div data-id="element-1855">
                    <h3 className="font-medium text-yellow-800 mb-1" data-id="element-1856">
                      Declining Application Trends
                    </h3>
                    <p className="text-sm text-yellow-700 mb-3" data-id="element-1857">
                      2 programs show declining application trends. The Master
                      of Public Administration program at UAE Business School
                      has seen a 5% decrease in applications compared to the
                      previous intake period.
                    </p>
                    <button className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-md hover:bg-yellow-700 transition-colors flex items-center" data-id="element-1858">
                      View Programs
                      <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1859" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-100" data-id="element-1860">
                <div className="flex items-start" data-id="element-1861">
                  <CalendarIcon className="w-5 h-5 text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1862" />
                  <div data-id="element-1863">
                    <h3 className="font-medium text-blue-800 mb-1" data-id="element-1864">
                      Intake Period Analysis
                    </h3>
                    <p className="text-sm text-blue-700 mb-3" data-id="element-1865">
                      Fall 2023 is projected to have a 15% increase in total
                      applications across all programs. Computer Science and
                      Nursing programs are expected to see the highest growth in
                      applications.
                    </p>
                    <button className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors flex items-center" data-id="element-1866">
                      View Forecast
                      <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1867" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-100" data-id="element-1868">
                <div className="flex items-start" data-id="element-1869">
                  <SparklesIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-1870" />
                  <div data-id="element-1871">
                    <h3 className="font-medium text-green-800 mb-1" data-id="element-1872">
                      High-Performing Programs
                    </h3>
                    <p className="text-sm text-green-700 mb-3" data-id="element-1873">
                      The Bachelor of Computer Science program at Emirates
                      Institute of Technology shows exceptional performance with
                      a 28% increase in applications and strong alignment with
                      industry needs.
                    </p>
                    <button className="px-3 py-1.5 bg-green-600 text-white text-xs rounded-md hover:bg-green-700 transition-colors flex items-center" data-id="element-1874">
                      View Best Practices
                      <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-1875" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Program Detail Modal */}
      <ProgramDetailModal isOpen={isDetailModalOpen} onClose={closeDetailModal} program={selectedProgram} data-id="element-1876" />
    </Layout>;
};
export default Programs;